import React, { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import { Button } from '@/components/ui/button'
import { Save } from 'lucide-react'
import { toast } from 'sonner'
import type { SubscriptionPlanConfig } from '@/types/admin'

const PLAN_LABELS: Record<string, { label: string; bg: string; border: string; color: string }> = {
  free: { label: 'Free', bg: 'bg-gray-50', border: 'border-gray-200', color: 'text-gray-700' },
  pro: { label: 'Pro', bg: 'bg-blue-50', border: 'border-blue-200', color: 'text-blue-700' },
  pro_max: { label: 'Pro Max', bg: 'bg-purple-50', border: 'border-purple-200', color: 'text-purple-700' },
}

const MB = 1024 * 1024

/** Human label for a megabyte count, so 51200 reads as "50 GB" not a wall of digits. */
function mbLabel(mb: number): string {
  if (mb === -1) return 'unlimited'
  if (mb >= 1024) {
    const gb = mb / 1024
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`
  }
  return `${mb} MB`
}

function PlanCard({
  config,
  onChange,
  onSave,
  saving,
}: {
  config: SubscriptionPlanConfig
  onChange: (updated: SubscriptionPlanConfig) => void
  onSave: () => void
  saving: boolean
}) {
  const style = PLAN_LABELS[config.plan] || PLAN_LABELS.free
  const isUnlimited = config.connectorLimit === -1

  // Mirrors the server-side invariant: the guarded upload INSERT compares a new
  // file against the total, so a per-file cap above the total would let a first
  // upload exceed the whole quota. Flagged inline, and Save is blocked below.
  const overPlanCap =
    config.storageLimitMb !== -1 &&
    config.perFileLimitBytes !== -1 &&
    config.perFileLimitBytes > config.storageLimitMb * MB

  const setField = <K extends keyof SubscriptionPlanConfig>(key: K, value: SubscriptionPlanConfig[K]) => {
    onChange({ ...config, [key]: value })
  }

  return (
    <div className={`rounded-lg border ${style.border} overflow-hidden`}>
      <div className={`${style.bg} px-5 py-3 border-b ${style.border} flex items-center justify-between`}>
        <div>
          <span className={`text-sm font-bold uppercase tracking-wide ${style.color}`}>
            {style.label}
          </span>
          {config.updatedAt && (
            <span className="text-xs text-gray-400 ml-2">
              updated {new Date(config.updatedAt).toLocaleString()}
            </span>
          )}
        </div>
        <Button
          onClick={onSave}
          disabled={saving || overPlanCap}
          size="sm"
          className="flex items-center gap-1.5"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Session Limit ($)</label>
            <input
              type="number"
              min={0}
              step={0.1}
              value={config.sessionLimit}
              onChange={e => setField('sessionLimit', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Monthly Limit ($)</label>
            <input
              type="number"
              min={0}
              step={1}
              value={config.monthlyLimit}
              onChange={e => setField('monthlyLimit', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Connector Limit</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={isUnlimited ? '' : config.connectorLimit}
                disabled={isUnlimited}
                placeholder={isUnlimited ? '∞' : ''}
                onChange={e => setField('connectorLimit', parseInt(e.target.value, 10) || 1)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
              />
              <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={isUnlimited}
                  onChange={e => setField('connectorLimit', e.target.checked ? -1 : 5)}
                  className="rounded border-gray-300"
                />
                Unlimited
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Session Duration (min)</label>
            <input
              type="number"
              min={1}
              value={config.sessionDurationMinutes}
              onChange={e => setField('sessionDurationMinutes', parseInt(e.target.value, 10) || 240)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Meeting Hours / period</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={config.meetingHoursLimit === -1 ? '' : config.meetingHoursLimit}
                disabled={config.meetingHoursLimit === -1}
                placeholder={config.meetingHoursLimit === -1 ? '∞' : ''}
                onChange={e => setField('meetingHoursLimit', parseInt(e.target.value, 10) || 1)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
              />
              <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.meetingHoursLimit === -1}
                  onChange={e => setField('meetingHoursLimit', e.target.checked ? -1 : 22)}
                  className="rounded border-gray-300"
                />
                Unlimited
              </label>
            </div>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-gray-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Storage
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Total Storage (MB)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={config.storageLimitMb === -1 ? '' : config.storageLimitMb}
                  disabled={config.storageLimitMb === -1}
                  placeholder={config.storageLimitMb === -1 ? '∞' : ''}
                  onChange={e => setField('storageLimitMb', parseInt(e.target.value, 10) || 1)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                />
                <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.storageLimitMb === -1}
                    onChange={e => setField('storageLimitMb', e.target.checked ? -1 : 512)}
                    className="rounded border-gray-300"
                  />
                  Unlimited
                </label>
              </div>
              <p className="mt-1 text-[11px] text-gray-400">{mbLabel(config.storageLimitMb)}</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Max File Size (MB)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  step={1}
                  // Stored in BYTES, edited in MB — nobody wants to type 209715200.
                  value={
                    config.perFileLimitBytes === -1
                      ? ''
                      : Math.round(config.perFileLimitBytes / MB)
                  }
                  disabled={config.perFileLimitBytes === -1}
                  placeholder={config.perFileLimitBytes === -1 ? '∞' : ''}
                  onChange={e =>
                    setField('perFileLimitBytes', (parseInt(e.target.value, 10) || 1) * MB)
                  }
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                />
                <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.perFileLimitBytes === -1}
                    onChange={e =>
                      setField('perFileLimitBytes', e.target.checked ? -1 : 50 * MB)
                    }
                    className="rounded border-gray-300"
                  />
                  Unlimited
                </label>
              </div>
              {overPlanCap && (
                <p className="mt-1 text-[11px] text-red-600">
                  Cannot exceed the total allowance ({mbLabel(config.storageLimitMb)}).
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PlanConfigPage() {
  const [loading, setLoading] = useState(true)
  const [configs, setConfigs] = useState<SubscriptionPlanConfig[]>([])
  const [savingPlan, setSavingPlan] = useState<string | null>(null)

  useEffect(() => {
    adminApi.getPlanConfigs()
      .then(setConfigs)
      .catch((e: any) => toast.error(e?.response?.data?.error || e?.message || 'Failed to load plan configs'))
      .finally(() => setLoading(false))
  }, [])

  const handleChange = (plan: string, updated: SubscriptionPlanConfig) => {
    setConfigs(prev => prev.map(c => c.plan === plan ? updated : c))
  }

  const handleSave = async (plan: string) => {
    const config = configs.find(c => c.plan === plan)
    if (!config) return

    setSavingPlan(plan)
    try {
      const { plan: _p, updatedAt: _ts, ...payload } = config
      const updated = await adminApi.updatePlanConfig(plan, payload)
      setConfigs(prev => prev.map(c => c.plan === plan ? updated : c))
      toast.success(`${PLAN_LABELS[plan]?.label || plan} plan config saved`)
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to save config')
    } finally {
      setSavingPlan(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">Subscription Plan Config</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Configure usage limits and session settings for each subscription plan.
          Changes are cached for 5 min — saves invalidate immediately.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {configs.map(config => (
          <PlanCard
            key={config.plan}
            config={config}
            onChange={updated => handleChange(config.plan, updated)}
            onSave={() => handleSave(config.plan)}
            saving={savingPlan === config.plan}
          />
        ))}
      </div>
    </div>
  )
}
